import { Fragment, type ReactNode } from 'react';
import { Trans } from 'react-i18next';
import type { TransitPolicyRuleCriteria } from '../models/TransitPolicy';
import { useModalTranslation } from '../hooks/useTranslation';
import { formatDurationLabel, getClassificationLabel, getFlapStateLabel, getMotionSensorStateLabel, getTriggerSourceLabel } from '../utils/doorPolicyTranslations';
import RfidChip from './RfidChip';

interface DoorPolicyRuleSummaryProps {
    criteria?: TransitPolicyRuleCriteria;
    deviceId?: string;
}

const DoorPolicyRuleSummary = ({ criteria, deviceId }: DoorPolicyRuleSummaryProps) => {
    const { t } = useModalTranslation();
    // The timeout limits how long the microchip criterion waits; it isn't
    // another condition that must match before the action can run.
    const entries = Object.entries(criteria ?? {}).filter(([key]) => key !== 'rfidTimeout');
    const timeout = criteria?.rfidTimeout;

    const clauses = entries.map(([key, value], index) => {
        const values = Array.isArray(value) ? value : [value];
        let translationKey: string;
        let nodes: ReactNode[];

        switch (key) {
            case 'timeRange':
                translationKey = 'time_between';
                nodes = values.map((item) => <strong>{String(item)}</strong>);
                break;
            case 'eventTriggerSource':
                translationKey = 'event_triggered_by';
                nodes = values.map((item) => <strong>{getTriggerSourceLabel(t, Number(item))}</strong>);
                break;
            case 'eventClassification':
                translationKey = 'appears_to_be';
                nodes = values.map((item) => <strong>{getClassificationLabel(t, Number(item))}</strong>);
                break;
            case 'motionSensorState':
                translationKey = 'motion_sensor_is';
                nodes = values.map((item) => <strong>{getMotionSensorStateLabel(t, Number(item))}</strong>);
                break;
            case 'flapState':
                translationKey = 'flap_is';
                nodes = values.map((item) => <strong>{getFlapStateLabel(t, Number(item))}</strong>);
                break;
            case 'rfidCode':
                translationKey = 'read_microchip';
                nodes = values.map((item) => <RfidChip compact deviceId={deviceId} rfidCode={String(item)} />);
                break;
            default:
                translationKey = 'unknown_criteria';
                nodes = [];
        }

        // Keep the rich values inside the translation so each language owns
        // its clause's word order, including words after the cat-name chips.
        const translatedValues = <span>{nodes.map((node, valueIndex) => (
            <Fragment key={valueIndex}>
                {valueIndex > 0 && ` ${t('door_policy.rule_summary.or')} `}
                {node}
            </Fragment>
        ))}</span>;

        return (
            <span key={key}>
                {index === 0 ? `${t('door_policy.rule_summary.if')} ` : `${t('door_policy.rule_summary.and')} `}
                <Trans
                    t={t}
                    ns="modals"
                    i18nKey={`door_policy.rule_summary.${translationKey}`}
                    components={{ values: translatedValues }}
                />
            </span>
        );
    });

    return (
        <>
            {clauses.length ? clauses : <span>{t('door_policy.rule_summary.always')}</span>}
            {timeout !== undefined && (
                <span>
                    {' ('}
                    <Trans
                        t={t}
                        ns="modals"
                        i18nKey="door_policy.rule_summary.waiting_up_to"
                        components={{ values: <strong>{timeout === 0
                            ? `0 ${t('door_policy.duration_units.seconds_short')}`
                            : formatDurationLabel(t, timeout, { short: true })}</strong> }}
                    />
                    {')'}
                </span>
            )}
        </>
    );
};

export default DoorPolicyRuleSummary;
